import React from "react";
import { useData } from "../components/context/data/DataState";
import { Link, useNavigate } from "react-router-dom";

import { Trophy, Edit, ArrowRight, Calendar, CheckCircle2, ChevronRight, Layers, ListTodo } from "lucide-react";
import { Separator } from "../components/ui/separator";
import { Card, CardContent } from "../components/ui/card";
import { Button } from "../components/ui/button";
import ClubSeasonInfo from "../components/user/ClubSeasonInfo";
import ChallengesLinear from "../components/user/ChallengesLinear";
import MainCarousel from "../components/user/MainCarousel";
import JoinCommunity from "./JoinCommunity";

const Home = () => {
  const {
    communityId,
    seasonId,
    challengeId,
    data,
    formattedDate,
    challengeTrackerData,
  } = useData();

  let navigate = useNavigate();
  let counter = 0;

  if (Object.keys(data.community || {}).length === 0) {
    return <JoinCommunity />;
  }

  const completedChallenges = () => {
    if (Array.isArray(challengeTrackerData))
      return (
        challengeTrackerData.filter(
          (challenge) => challenge.percent_complete === 100
        ).length || 0
      );
  };

  return (
    <div className="pb-24">
      <MainCarousel />
      <Separator className="bg-gray-700" />
      <ClubSeasonInfo />
      <Separator className="bg-gray-700" />
      {data.community[communityId]?.season[seasonId]?.challenge &&
        Object.values(data.community[communityId]?.season[seasonId]?.challenge)
          .length ? (
        <>
          <div className="p-4 space-y-6 max-w-5xl mx-auto">
            {/* Stats Section */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <Card className="bg-gradient-to-br from-green-500 to-emerald-600 text-white border-none shadow-lg">
                <CardContent className="p-6 flex items-center gap-4">
                  <div className="bg-white/20 p-4 rounded-full backdrop-blur-sm">
                    <Trophy className="h-8 w-8" />
                  </div>
                  <div className="flex-1">
                    <div className="text-green-100 font-medium mb-1">Completed Challenges</div>
                    <div className="flex items-baseline gap-2">
                      <span className="text-4xl font-bold">
                        {Array.isArray(challengeTrackerData)
                          ? completedChallenges()
                          : 0}
                      </span>
                      <span className="text-xl text-green-100/80">
                        /
                        {Object.values(
                          data.community[communityId]?.season[seasonId]?.challenge
                        ).length || 0}
                      </span>
                    </div>
                  </div>
                </CardContent>
              </Card>

              <Card
                className="bg-gradient-to-br from-blue-500 to-indigo-600 text-white cursor-pointer hover:shadow-xl transition-all duration-300 transform hover:-translate-y-1 border-none"
                onClick={() => navigate("/all_challenges")}
              >
                <CardContent className="p-6 flex items-center justify-between h-full">
                  <div>
                    <div className="text-blue-100 font-medium mb-1">Explore More</div>
                    <div className="text-3xl font-bold">View All Challenges</div>
                  </div>
                  <div className="bg-white/20 p-3 rounded-full backdrop-blur-sm">
                    <ArrowRight className="h-6 w-6" />
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* Progress Graph */}
            <div className="bg-white rounded-xl shadow-sm border p-4">
              <ChallengesLinear />
            </div>

            {/* Current Challenge Header */}
            <div className="flex items-center justify-between bg-white p-4 rounded-xl shadow-sm border border-l-4 border-l-blue-500">
              <div>
                <h2 className="text-lg font-bold text-gray-900">
                  {data.community[communityId]?.season[seasonId]?.challenge[challengeId]?.challenge_name}
                </h2>
                <p className="text-gray-500 text-sm">
                  {data.community[communityId]?.season[seasonId]?.season_name}
                </p>
              </div>
              <div className="text-right bg-gray-50 px-3 py-1 rounded-lg">
                <p className="text-xs text-gray-500 uppercase font-semibold">Date</p>
                <p className="font-medium text-gray-900">
                  {formattedDate.toLocaleDateString("en-US", {
                    month: "short",
                    day: "numeric",
                  })}
                </p>
              </div>
            </div>

            {/* Activities Grid */}
            <div className="space-y-4">
              <h3 className="text-lg font-semibold text-gray-800 flex items-center gap-2">
                <ListTodo className="h-5 w-5 text-blue-500" />
                Today's Activities
              </h3>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {/* Events not in group */}
                {data.community[communityId]?.season[seasonId]?.challenge[challengeId]?.event
                  .filter(notingroup)
                  .map((eventitem, idx) => (
                    <Link to={`/edit/${eventitem.event_id}`} key={eventitem.event_id} className="block group">
                      <Card className="h-full hover:shadow-md transition-all duration-200 border-l-4 border-l-purple-500 group-hover:border-l-purple-600">
                        <CardContent className="p-4 flex items-start justify-between">
                          <div className="flex gap-3">
                            <div className="bg-purple-50 p-2 rounded-lg group-hover:bg-purple-100 transition-colors">
                              <Calendar className="h-5 w-5 text-purple-600" />
                            </div>
                            <div>
                              <h4 className="font-semibold text-gray-900 group-hover:text-purple-700 transition-colors">
                                {eventitem.event_name}
                              </h4>
                              <p className="text-sm text-gray-500">Event • {eventitem.event_frequency}</p>
                            </div>
                          </div>
                          <Button variant="ghost" size="icon" className="text-gray-400 group-hover:text-purple-600">
                            <ChevronRight className="h-5 w-5" />
                          </Button>
                        </CardContent>
                      </Card>
                    </Link>
                  ))}

                {/* Tasks not in group */}
                {data.community[communityId]?.season[seasonId]?.challenge[challengeId]?.task
                  .filter(notingroup)
                  .map((taskitem, idx) => (
                    <Link to={`/edit/${taskitem.task_id}`} key={taskitem.task_id} className="block group">
                      <Card className="h-full hover:shadow-md transition-all duration-200 border-l-4 border-l-blue-500 group-hover:border-l-blue-600">
                        <CardContent className="p-4 flex items-start justify-between">
                          <div className="flex gap-3">
                            <div className="bg-blue-50 p-2 rounded-lg group-hover:bg-blue-100 transition-colors">
                              <CheckCircle2 className="h-5 w-5 text-blue-600" />
                            </div>
                            <div>
                              <h4 className="font-semibold text-gray-900 group-hover:text-blue-700 transition-colors">
                                {taskitem.task_name}
                              </h4>
                              <p className="text-sm text-gray-500">
                                Task • {taskitem.task_quantity} {taskitem.task_unit}
                              </p>
                            </div>
                          </div>
                          <Button variant="ghost" size="icon" className="text-gray-400 group-hover:text-blue-600">
                            <ChevronRight className="h-5 w-5" />
                          </Button>
                        </CardContent>
                      </Card>
                    </Link>
                  ))}

                {/* Groups */}
                {data.community[communityId]?.season[seasonId]?.challenge[challengeId]?.group.map((groupitem) => (
                  <div key={groupitem.group_id} className="block md:col-span-2">
                    <Card className="h-full border-l-4 border-l-amber-500">
                      <CardContent className="p-4">
                        <div className="flex items-start justify-between mb-3">
                          <div className="flex gap-3">
                            <div className="bg-amber-50 p-2 rounded-lg transition-colors">
                              <Layers className="h-5 w-5 text-amber-600" />
                            </div>
                            <div>
                              <h4 className="font-semibold text-gray-900 transition-colors">
                                {groupitem.group_name}
                              </h4>
                              <p className="text-sm text-gray-500">Group Activity</p>
                            </div>
                          </div>
                        </div>

                        <div className="pl-12 space-y-2">
                          {/* Group Tasks */}
                          {data.community[communityId]?.season[seasonId]?.challenge[challengeId]?.task
                            .filter(t => t.group_id === groupitem.group_id)
                            .map(task => (
                              <Link
                                to={`/edit/${task.task_id}`}
                                key={task.task_id}
                                className="flex items-center justify-between gap-2 text-sm text-gray-600 bg-gray-50 p-2 rounded hover:bg-gray-100 transition-colors group cursor-pointer"
                              >
                                <div className="flex items-center gap-2">
                                  <CheckCircle2 className="h-3 w-3 text-blue-500" />
                                  <span>{task.task_name}: {task.task_quantity} {task.task_unit}</span>
                                </div>
                                <ChevronRight className="h-4 w-4 text-gray-400 group-hover:text-gray-600" />
                              </Link>
                            ))}

                          {/* Group Events */}
                          {data.community[communityId]?.season[seasonId]?.challenge[challengeId]?.event
                            .filter(e => e.group_id === groupitem.group_id)
                            .map(event => (
                              <Link
                                to={`/edit/${event.event_id}`}
                                key={event.event_id}
                                className="flex items-center justify-between gap-2 text-sm text-gray-600 bg-gray-50 p-2 rounded hover:bg-gray-100 transition-colors group cursor-pointer"
                              >
                                <div className="flex items-center gap-2">
                                  <Calendar className="h-3 w-3 text-purple-500" />
                                  <span>{event.event_name}</span>
                                </div>
                                <ChevronRight className="h-4 w-4 text-gray-400 group-hover:text-gray-600" />
                              </Link>
                            ))}
                        </div>
                      </CardContent>
                    </Card>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </>
      ) : (
        <div className="flex flex-col items-center justify-center h-64 text-gray-500 space-y-4">
          <div className="bg-gray-100 p-4 rounded-full">
            <Trophy className="h-8 w-8 text-gray-400" />
          </div>
          <p className="text-lg font-medium">No Challenges Active</p>
          <p className="text-sm">Check back later for new activities!</p>
        </div>
      )}
    </div>
  );
};

function notingroup(i) {
  return i.group_id === "" || i.group_id === undefined || i.group_id === null;
}

export default Home;
